// 🔴 L2 (N°876) — la liste GÉNÉRIQUE des devis d'un dossier, EXTRAITE.
//
// ⚠️ `DevisFacturesTab` mêle cette liste à des gestes AMO (routes
// `/metier/tef/dossiers/{x}/mandat-amo|devis-amo|contrat-amo/generer`) et à des
// branches `isTEF` : il appartient à ce métier-là (N°867). Ici, ⛔ rien de tout
// cela : la lecture `GET /api/metier/devis?dossier_id=` et la navigation déjà
// en place (`devis-document`, `devis-wizard`). `DevisFacturesTab` ⛔ n'est pas
// modifié : TEF et OMV le gardent tel quel.
import { useEffect, useState } from "react";
import { creerFactureDepuisDevis, devisDuDossier, eligibiliteFactures, emettreDevis, pdfDevis } from "./api";
import { devisStatutLabel, estModifiableDevis } from "../../../utils/devisStatut";
import { FormEchelonnement } from "../../../components/core/EchelonnementFacture";

const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`);

export function ListeDevisDossier({ dossierId, onNavigate }) {
  const [devis, setDevis] = useState(null);
  const [erreur, setErreur] = useState(null);
  // 🔴 N°966 — état de facturation PAR devis (capacité partagée) : `facture_id`
  //    (déjà facturé) ou `eligible` (facturable). ⛔ Rien de spécifique Mario.
  const [etats, setEtats] = useState({});
  const [occupe, setOccupe] = useState(null);
  // 🔴 N°975 — relecture après un geste serveur (émission) : ⛔ aucun état fabriqué ici.
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let vivant = true;
    devisDuDossier(dossierId)
      .then((l) => vivant && setDevis(Array.isArray(l) ? l : []))
      .catch((e) => { if (vivant) { setErreur(e.message); setDevis([]); } });
    return () => { vivant = false; };
  }, [dossierId, rev]);

  useEffect(() => {
    if (!devis || !devis.length) { setEtats({}); return; }
    let vivant = true;
    eligibiliteFactures(devis.map((d) => d.id).filter(Boolean))
      .then((r) => vivant && setEtats(r?.etats || {}))
      .catch(() => vivant && setEtats({}));   // ⚠️ sans état ⇒ pas de bouton (⛔ jamais un faux « éligible »)
    return () => { vivant = false; };
  }, [devis]);

  const facturer = async (d, options) => {
    setErreur(null); setOccupe(d.id);
    try {
      const data = await (options ? creerFactureDepuisDevis(d.id, options) : creerFactureDepuisDevis(d.id));   // POST partagé /factures/depuis-devis
      if (onNavigate) onNavigate("factures", { factureId: data.id, dossierId });
    } catch (e) {
      // 🔑 Le double-clic/déjà-facturé arrive ici (409) : on DIT le motif, ⛔ pas de silence.
      setErreur(e.message);
    } finally {
      setOccupe(null);
    }
  };

  // 🔴 N°975 — N°976 a montré qu'AUCUN écran de la fiche artisan n'émettait un devis : un
  //    brouillon n'étant pas facturable, la chaîne s'arrêtait là. Émission par la route
  //    native partagée ; le serveur décide (identité légale, numéro, GED), on DIT le motif.
  const emettre = async (d) => {
    setErreur(null); setOccupe(d.id);
    try { await emettreDevis(d.id); setRev((x) => x + 1); }
    catch (e) { setErreur(e.message); }
    finally { setOccupe(null); }
  };

  // 🔴 N°1031 — mêmes gestes que la fiche devis / la grille : Voir · Modifier · Télécharger (puis l'action d'état).
  const voir = (d) => onNavigate && onNavigate("devis-document", { devisId: d.id, dossierId, from: "dossier" });
  const modifier = (d) => onNavigate && onNavigate("devis-wizard", { devisId: d.id, dossierId });
  const telecharger = async (d) => {
    setErreur(null); setOccupe(d.id);
    try {
      const url = URL.createObjectURL(await pdfDevis(d.id));
      const a = document.createElement("a");
      a.href = url; a.download = `Devis_${String(d.numero || d.numero_provisoire || d.id).replace(/[\\/]/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setErreur(e.message); }
    finally { setOccupe(null); }
  };
  const PETIT = { height: 32, background: "transparent", border: "0.5px solid var(--oh-border)",
                  color: "var(--oh-text-secondary)" };

  return (
    <div data-testid="artisan-devis">
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "var(--oh-text)", fontWeight: 500 }}>Devis</div>
        {onNavigate && (
          <button type="button" data-testid="artisan-devis-nouveau"
                  onClick={() => onNavigate("devis-wizard", { dossierId, devisId: "new" })}
                  className="rounded-lg px-3 text-sm"
                  style={{ height: 36, background: "transparent",
                           border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)" }}>
            Nouveau devis
          </button>
        )}
      </div>
      {erreur && <div className="text-sm" role="alert" style={{ color: "var(--oh-error-text)" }}>{erreur}</div>}
      {devis === null ? (
        <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>Chargement…</div>
      ) : !devis.length ? (
        <div className="text-sm" data-testid="artisan-devis-aucun" style={{ color: "var(--oh-text-secondary)" }}>
          Aucun devis sur ce dossier.
        </div>
      ) : (
        <div className="space-y-1">
          {devis.map((d) => {
            const etat = etats[d.id] || {};
            return (
              <div key={d.id} data-testid="artisan-devis-bloc">
              <div className="flex items-center gap-2 rounded-lg px-3 py-2"
                   style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)" }}>
                <button type="button" data-testid="artisan-devis-ligne"
                        onClick={() => onNavigate && onNavigate("devis-document",
                          { devisId: d.id, dossierId, from: "dossier" })}
                        className="flex-1 flex justify-between text-sm"
                        style={{ background: "transparent", color: "var(--oh-text)" }}>
                  <span>{d.numero || d.numero_provisoire || "Brouillon"}</span>
                  <span style={{ color: "var(--oh-text-secondary)" }}>{devisStatutLabel(d, null, d.statut)}</span>
                  <span>{eur(d.total_ttc)}</span>
                </button>
                <button type="button" data-testid="artisan-devis-voir" onClick={() => voir(d)}
                        className="rounded-lg px-3 text-sm whitespace-nowrap" style={PETIT}>Voir</button>
                {estModifiableDevis(d.statut) && (
                  <button type="button" data-testid="artisan-devis-modifier" onClick={() => modifier(d)}
                          className="rounded-lg px-3 text-sm whitespace-nowrap" style={PETIT}>Modifier</button>
                )}
                <button type="button" data-testid="artisan-devis-telecharger" disabled={occupe === d.id}
                        onClick={() => telecharger(d)}
                        className="rounded-lg px-3 text-sm whitespace-nowrap" style={PETIT}>Télécharger</button>
                {/* 🔴 N°966 — Devis → Facture (capacité partagée). Déjà facturé ⇒ « Voir »,
                     facturable ⇒ « Créer la facture ». La facture + son PDF apparaissent
                     ensuite dans les Pièces (GED) du dossier. */}
                {etat.eligible && !etat.facture_id ? (
                  <button type="button" data-testid="artisan-creer-facture" disabled={occupe === d.id}
                          onClick={() => facturer(d)}
                          className="rounded-lg px-3 text-sm whitespace-nowrap"
                          style={{ height: 32, background: "transparent",
                                   border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)" }}>
                    {occupe === d.id ? "Création…" : "Créer une facture"}
                  </button>
                ) : d.statut === "draft" && !etat.facture_id ? (
                  <button type="button" data-testid="artisan-emettre-devis" disabled={occupe === d.id}
                          onClick={() => emettre(d)}
                          className="rounded-lg px-3 text-sm whitespace-nowrap"
                          style={{ height: 32, background: "var(--oh-accent-solid)", border: "none",
                                   color: "var(--oh-accent-contrast)" }}>
                    {occupe === d.id ? "Validation…" : "Valider le devis"}
                  </button>
                ) : null}
              </div>
              {/* LOT D — facture d'acompte / intermédiaire, offerte par le serveur (`echelonnement_eligible`,
                  absent pour un profil sans la capacité). Le solde se crée depuis la facture d'acompte. */}
              {etat.echelonnement_possible && etat.echelonnement_eligible && Number(etat.pourcentage_restant) > 0 && (
                <div className="rounded-lg px-3 py-2 ml-6 mt-1" data-testid="artisan-echelonnement"
                     style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)" }}>
                  <FormEchelonnement types={["acompte", "intermediaire"]} pourcentageRestant={etat.pourcentage_restant}
                                     occupe={occupe === d.id} testidPrefix="artisan-echelon"
                                     onCreer={(type, pourcentage) => facturer(d, { type_facture: type, pourcentage })} />
                </div>
              )}
              {/* 🔴 N°1031 — VISITE → DEVIS → FACTURE : la facture apparaît SOUS son devis. */}
              {etat.facture_id && (
                <div data-testid="artisan-facture-ligne" className="flex items-center gap-2 rounded-lg px-3 py-2 ml-6 mt-1"
                     style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)" }}>
                  <span className="flex-1 text-sm" style={{ color: "var(--oh-text)" }}>↳ Facture {etat.facture_numero || "brouillon"}</span>
                  <button type="button" data-testid="artisan-voir-facture"
                          onClick={() => onNavigate && onNavigate("factures", { factureId: etat.facture_id, dossierId })}
                          className="rounded-lg px-3 text-sm whitespace-nowrap" style={PETIT}>
                    Voir la facture
                  </button>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ListeDevisDossier;
